import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { useAppScopeStore } from '@frontend/scope';
import { LocationCreditData } from '../types';
import { getAIRCreditUsagePercent, getAIRCreditUsageVariant } from '../utils';

interface UseAICallCreditDataOptions {
  locationIds: string[];
}

export function useAICallCreditData({ locationIds }: UseAICallCreditDataOptions) {
  const { getLocationName } = useAppScopeStore();

  const { data, isLoading } = PlatformLocationFeatureV1.Queries.useGetLocationFeatureCreditDetailsQuery({
    request: {
      locationIds,
      featureEnum: Feature.TRUELARK_AI_VOICE,
    },
    options: {
      enabled: locationIds.length > 0,
      cacheTime: 0,
      staleTime: 0,
    },
  });

  const computedData = useMemo(() => {
    if (!data?.locationCreditDetails) {
      return {
        locations: [],
        exhaustedLocations: [],
        criticalLocations: [],
        warningLocations: [],
      };
    }

    const locations: LocationCreditData[] = Object.entries(data.locationCreditDetails).map(([locationId, details]) => {
      const totalCredits = details.credits?.totalCredits ?? 0;
      const consumedCredits = details.credits?.consumedCredits ?? 0;
      const usagePercent = getAIRCreditUsagePercent(consumedCredits, totalCredits);

      return {
        locationId,
        locationName: getLocationName(locationId) || locationId,
        creditExhaustedAt: details.creditExhaustedAt,
        consumedCredits,
        totalCredits,
        usagePercent,
        variant: getAIRCreditUsageVariant(usagePercent),
        forecastedEndDate: details.credits?.forecastedEndDate,
        billingEndDate: details.credits?.billingEndDate,
      };
    });

    const exhaustedLocations = locations.filter(({ variant }) => variant === 'exhausted');
    const criticalLocations = locations.filter(({ variant }) => variant === 'critical');
    const warningLocations = locations.filter(({ variant }) => variant === 'warning');

    return {
      locations,
      exhaustedLocations,
      criticalLocations,
      warningLocations,
    };
  }, [data?.locationCreditDetails, getLocationName]);

  return {
    ...computedData,
    isLoading,
  };
}
